// Packages and components
import React from 'react';
import { Box, Button, DropButton } from 'grommet';
import { LinkPrevious, MoreVertical } from 'grommet-icons';
import DeleteGameModal from '@features/Schedule/DeleteGameModal';

// Helpers and utilities
import { isWithinHours } from '@helpers/dateUtils';

// Contexts and hooks
import useGame from '@hooks/useGame';
import { useModal } from '@components/ModalContext';

// Types, interfaces, and enums
import type { GamePageActionMenuProps } from './game.d';
import { GameStatus } from '@features/Schedule/schedule.d';

// Styles
const styledButton: React.CSSProperties = {
  background: '#fff',
  borderRadius: '100%',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.20)',
  cursor: 'pointer',
  padding: '8px',
};

const GamePageActionMenu: React.FC<GamePageActionMenuProps> = ({
  gameData,
  history,
  isTeamAdmin,
}) => {
  // Hooks
  const { deleteGame } = useGame();
  const { showModal, hideModal } = useModal();

  // Constants
  const isGameFinal = () => {
    const isStatusEnded = gameData?.scoreGameState === GameStatus.ENDED;
    const isOverTimeLimit = !isWithinHours(gameData?.J_tsStart, 72);

    if (isStatusEnded || isOverTimeLimit) {
      return true;
    }

    return false;
  };

  // Handlers
  const handleDeleteGame = async () => {
    try {
      await deleteGame(gameData?.DNOR_teamId, gameData?._id).then(() => {
        history.push(
          gameData?.DNOR_teamId ? `/${gameData?.DNOR_teamId}` : '/home'
        );
      });
    } finally {
      hideModal();
    }
  };

  return (
    <Box
      data-testid="game-page-action-menu"
      direction="row"
      justify="between"
      pad={{ horizontal: '16px', top: '16px' }}
    >
      <Box
        data-testid="game-page-action-menu__back"
        onClick={() => history.goBack()}
        style={styledButton}
      >
        <LinkPrevious size="24px" color="black" />
      </Box>

      {isTeamAdmin && isGameFinal() && (
        <Box>
          <DropButton
            data-testid="game-page-action-menu__drop-button"
            dropAlign={{ top: 'bottom', right: 'right' }}
            dropContent={
              <Box pad="small" background="white" round="small">
                <Button
                  label="Delete game"
                  onClick={() => {
                    showModal(
                      <DeleteGameModal
                        body="All videos associated with this game will be permanently deleted. This action cannot be undone."
                        onClose={() => {
                          hideModal();
                        }}
                        onDeleteGame={() => {
                          handleDeleteGame();
                        }}
                      />,
                      {
                        onClose: () => hideModal(),
                        onGoBack: undefined,
                        isRelativeHeight: true,
                        isConfirmation: true,
                      }
                    );
                  }}
                />
              </Box>
            }
            icon={<MoreVertical size="24px" color="black" />}
            style={styledButton}
          />
        </Box>
      )}
    </Box>
  );
};
export default GamePageActionMenu;
